import { useState, useEffect } from "react";
import { X, FileText, CheckCircle2, AlertTriangle, AlertOctagon, Minus, Loader2, ChevronDown, ChevronRight, BrainCircuit } from "lucide-react";
import { formatCurrency } from "../lib/calculations";
import { supabase } from "../lib/supabase";
import { MarkdownAnalysis } from "./MarkdownAnalysis";
import { logError } from "../lib/logError";
import {
    canRunScheduleCheckAgain,
    isFailedScheduleCheck,
    scheduleCheckDateLine,
    scheduleCheckStatusLabel,
} from "../lib/scheduleCheckState";

interface LineItem {
    field: string;
    claim_value?: number;
    schedule_value?: number;
    status: "match" | "partial" | "discrepancy";
    difference?: number;
    note?: string;
    acknowledged?: boolean;
}

interface AnalysisResult {
    overall_status?: "match" | "partial" | "discrepancy";
    summary?: string;
    extracted_values?: {
        payment_schedule_total?: number | null;
        claimed_amount?: number | null;
        retention?: number | null;
        gst?: number | null;
        net_payment?: number | null;
    };
    analysis_markdown?: string;
    line_items?: LineItem[];
    flags?: string[];
    error?: string;
    _thoughts?: string;
}

interface Props {
    result: AnalysisResult;
    claimNumber: number;
    analyzedAt: string | null;
    paymentScheduleUrl?: string | null;
    reportId?: string;
    /** schedule_check_status of the row, so a failed check is not read as a discrepancy */
    checkStatus?: string | null;
    onUpdate?: (updatedResult: AnalysisResult) => void;
    onClose: () => void;
    onReupload?: () => void;
    /** Runs the check again against the schedule that is already uploaded */
    onRetry?: () => void;
}

const FAILED_CONFIG = {
    color: "#b45309", // slop-lint: allow design.color-literal amber-700 for the failed state, 5.02:1 on white; --color-warning is too light for text
    bg: "rgba(180,83,9,0.08)", // slop-lint: allow design.color-literal tint of the failed state colour above
    border: "rgba(180,83,9,0.2)", // slop-lint: allow design.color-literal border of the failed state colour above
    icon: AlertTriangle,
};

const STATUS_CONFIG = {
    match: {
        color: "#16a34a",
        bg: "rgba(22,163,74,0.08)",
        border: "rgba(22,163,74,0.2)",
        icon: CheckCircle2,
        label: "All Match",
    },
    partial: {
        color: "#f59e0b",
        bg: "rgba(245,158,11,0.08)",
        border: "rgba(245,158,11,0.2)",
        icon: AlertTriangle,
        label: "Partial Match",
    },
    discrepancy: {
        color: "#ef4444",
        bg: "rgba(239,68,68,0.08)",
        border: "rgba(239,68,68,0.2)",
        icon: AlertOctagon,
        label: "Discrepancy",
    },
};

export function ScheduleCheckReportModal({ result: initialResult, claimNumber, analyzedAt, paymentScheduleUrl, reportId, checkStatus, onUpdate, onClose, onReupload, onRetry }: Props) {
    const [result, setResult] = useState<AnalysisResult>(initialResult);
    const [savingField, setSavingField] = useState<string | null>(null);
    const [showThoughts, setShowThoughts] = useState(false);

    useEffect(() => {
        setResult(initialResult);
    }, [initialResult]);

    const handleAcknowledge = async (index: number) => {
        if (!reportId || !result.line_items) return;
        const item = result.line_items[index];
        if (item.status === 'match') return;

        setSavingField(item.field);
        const updatedItems = [...result.line_items];
        updatedItems[index] = { ...item, acknowledged: !item.acknowledged };
        const updatedResult = { ...result, line_items: updatedItems };

        try {
            const { error } = await supabase
                .from("claims_reports")
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                .update({ schedule_check_result: updatedResult as any })
                .eq("id", reportId);

            if (error) throw error;

            setResult(updatedResult);
            if (onUpdate) onUpdate(updatedResult);
        } catch (err) {
            logError("Failed to acknowledge discrepancy:", err);
        } finally {
            setSavingField(null);
        }
    };

    const failed = isFailedScheduleCheck(result, checkStatus);
    const overall = failed
        ? { ...FAILED_CONFIG, label: scheduleCheckStatusLabel(true, null) }
        : { ...(STATUS_CONFIG[result.overall_status || "discrepancy"] || STATUS_CONFIG.discrepancy), label: scheduleCheckStatusLabel(false, result.overall_status) };
    const Icon = overall.icon;
    const canRetry = canRunScheduleCheckAgain(failed, paymentScheduleUrl, Boolean(onRetry));

    const fmtDate = (iso: string) => {
        const d = new Date(iso);
        return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal"
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: "680px", maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column" }}
            >
                {/* Header */}
                <div
                    className="modal-header"
                    style={{ padding: "16px 20px", borderBottom: `3px solid ${overall.color}` }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div
                            style={{
                                width: "36px", height: "36px", borderRadius: "8px",
                                background: overall.bg, border: `1px solid ${overall.border}`,
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                        >
                            <Icon size={18} color={overall.color} />
                        </div>
                        <div>
                            <h3 className="modal-title" style={{ margin: 0, fontSize: "1rem" }}>
                                Schedule Check: Claim #{claimNumber}
                            </h3>
                            <p style={{ margin: 0, fontSize: "0.72rem", color: "var(--text-muted)" }}>
                                {scheduleCheckDateLine(failed, analyzedAt, result, fmtDate)} • Status: <span style={{ color: overall.color, fontWeight: 700 }}>{overall.label}</span>
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: "4px" }}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Scrollable body */}
                <div style={{ overflow: "auto", flex: 1, padding: "16px 20px" }}>
                    {/* View PDF Button */}
                    {paymentScheduleUrl && (
                        <div style={{ marginBottom: "16px", display: "flex", justifyContent: "flex-end" }}>
                            <a
                                href={paymentScheduleUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-secondary"
                                style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "0.75rem", padding: "6px 12px", textDecoration: "none" }}
                            >
                                <FileText size={14} />
                                View Uploaded Schedule
                            </a>
                        </div>
                    )}

                    {/* Error / Summary */}
                    {failed ? (
                        <div
                            style={{
                                padding: "12px 14px", borderRadius: "8px",
                                background: FAILED_CONFIG.bg,
                                border: `1px solid ${FAILED_CONFIG.border}`,
                                marginBottom: "16px", fontSize: "0.82rem", lineHeight: 1.5,
                            }}
                        >
                            <p style={{ margin: 0, color: "var(--text-primary)" }}>
                                <strong>Check did not finish:</strong> {result.error || "The schedule check stopped before it could compare the schedule with the claim."}
                            </p>
                        </div>
                    ) : result.analysis_markdown ? (
                        /* Rich Markdown Analysis - PRIMARY */
                        <div style={{ marginBottom: "16px" }}>
                            <MarkdownAnalysis content={result.analysis_markdown} />
                        </div>
                    ) : (
                        /* Fallback: simple summary for legacy results */
                        <div
                            style={{
                                padding: "12px 14px", borderRadius: "8px",
                                background: overall.bg,
                                border: `1px solid ${overall.border}`,
                                marginBottom: "16px", fontSize: "0.82rem", lineHeight: 1.5,
                            }}
                        >
                            <p style={{ margin: 0, color: "var(--text-primary)" }}>
                                {result.summary || "No summary provided."}
                            </p>
                        </div>
                    )}

                    {/* AI Thoughts Accordion */}
                    {result._thoughts && (
                        <div style={{ marginBottom: "16px", borderRadius: "8px", border: "1px solid var(--border-color)", overflow: "hidden" }}>
                            <button
                                onClick={() => setShowThoughts((prev) => !prev)}
                                style={{
                                    width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                                    padding: "10px 14px", background: "var(--bg-secondary)", border: "none", cursor: "pointer",
                                    fontSize: "0.80rem", fontWeight: 600, color: "var(--text-secondary)",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <BrainCircuit size={15} color="#8b5cf6" />
                                    AI Reasoning Process
                                </div>
                                {showThoughts ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                            {showThoughts && (
                                <div
                                    style={{
                                        padding: "12px 14px", background: "rgba(139,92,246,0.03)", borderTop: "1px solid var(--border-color)",
                                        fontSize: "0.75rem", lineHeight: 1.6, color: "var(--text-secondary)", whiteSpace: "pre-wrap",
                                    }}
                                >
                                    {result._thoughts}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Extracted Values Grid */}
                    {result.extracted_values && (
                        <div style={{ marginBottom: "16px" }}>
                            <h4 style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-secondary)", marginBottom: "8px" }}>
                                Extracted from Payment Schedule
                            </h4>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "8px" }}>
                                {[
                                    { label: "Schedule Total", value: result.extracted_values.payment_schedule_total },
                                    { label: "Claimed Amount", value: result.extracted_values.claimed_amount },
                                    { label: "Retention", value: result.extracted_values.retention },
                                    { label: "GST", value: result.extracted_values.gst },
                                    { label: "Net Payment", value: result.extracted_values.net_payment },
                                ].map((item) => (
                                    <div
                                        key={item.label}
                                        style={{
                                            padding: "8px 10px", borderRadius: "6px",
                                            background: "var(--bg-secondary)", border: "1px solid var(--border-color-light)",
                                        }}
                                    >
                                        <div style={{ fontSize: "0.65rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                                            {item.label}
                                        </div>
                                        <div data-seq-private="" style={{ fontSize: "0.88rem", fontWeight: 700, color: "var(--text-primary)", marginTop: "2px" }}>
                                            {item.value !== null && item.value !== undefined ? formatCurrency(item.value) : "—"}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Line Items Comparison Table */}
                    {result.line_items && result.line_items.length > 0 && (
                        <div style={{ marginBottom: "16px" }}>
                            <h4 style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-secondary)", marginBottom: "8px" }}>
                                Line-by-Line Comparison
                            </h4>
                            <div style={{ borderRadius: "8px", border: "1px solid var(--border-color)", overflow: "hidden" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
                                    <thead>
                                        <tr style={{ background: "var(--bg-secondary)" }}>
                                            <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", color: "var(--text-muted)" }}>Field</th>
                                            <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", color: "var(--text-muted)" }}>Claim</th>
                                            <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", color: "var(--text-muted)" }}>Schedule</th>
                                            <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", color: "var(--text-muted)" }}>Diff</th>
                                            <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", color: "var(--text-muted)", width: "70px" }}>Status</th>
                                            <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", color: "var(--text-muted)", width: "50px" }}>Ack</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.line_items.map((item, i) => {
                                            const isAck = item.acknowledged;
                                            const cfg = isAck
                                                ? { color: "#16a34a", bg: "rgba(22,163,74,0.08)", border: "rgba(22,163,74,0.15)" }
                                                : (STATUS_CONFIG[item.status] || STATUS_CONFIG.discrepancy);

                                            // Check if the field is likely a claim number rather than a currency amount
                                            const fieldLabel = item.field.toLowerCase();
                                            const isCurrency = !(
                                                fieldLabel.includes('number') ||
                                                fieldLabel.includes('no.') ||
                                                fieldLabel.endsWith(' no') ||
                                                fieldLabel === 'claim' ||
                                                fieldLabel === 'claim no' ||
                                                fieldLabel.includes('pct') ||
                                                fieldLabel.includes('percentage') ||
                                                fieldLabel.includes('%')
                                            );

                                            const formatValue = (val: number) => isCurrency ? formatCurrency(val) : String(val);

                                            return (
                                                <tr key={i} style={{ borderTop: "1px solid var(--border-color-light)", opacity: isAck ? 0.7 : 1 }}>
                                                    <td style={{ padding: "8px 10px", fontWeight: 600 }}>{item.field}</td>
                                                    <td style={{ padding: "8px 10px", textAlign: "right", textDecoration: isAck && item.claim_value !== item.schedule_value ? "line-through" : "none", color: isAck ? "var(--text-muted)" : "inherit" }}>{item.claim_value !== undefined && item.claim_value !== null ? formatValue(item.claim_value) : "-"}</td>
                                                    <td style={{ padding: "8px 10px", textAlign: "right" }}>{item.schedule_value !== undefined && item.schedule_value !== null ? formatValue(item.schedule_value) : "-"}</td>
                                                    <td style={{ padding: "8px 10px", textAlign: "right", color: item.difference ? cfg.color : undefined, fontWeight: item.difference && !isAck ? 600 : undefined }}>
                                                        {item.difference ? formatValue(Math.abs(item.difference)) : <Minus size={12} color="var(--text-muted)" />}
                                                    </td>
                                                    <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                                        <span style={{
                                                            display: "inline-block", padding: "2px 8px", borderRadius: "10px",
                                                            fontSize: "0.65rem", fontWeight: 700,
                                                            background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                                                        }}>
                                                            {isAck ? "ACK" : (item.status === "match" ? "✓" : item.status === "partial" ? "~" : "✗")}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                                        {item.status !== "match" && (
                                                            <button
                                                                onClick={() => handleAcknowledge(i)}
                                                                disabled={savingField === item.field}
                                                                style={{
                                                                    background: isAck ? "rgba(22,163,74,0.15)" : "transparent",
                                                                    color: isAck ? "#16a34a" : "var(--text-muted)",
                                                                    border: isAck ? "1px solid rgba(22,163,74,0.3)" : "1px dashed var(--border-color)",
                                                                    borderRadius: "4px",
                                                                    width: "24px", height: "24px",
                                                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                                    cursor: savingField === item.field ? "not-allowed" : "pointer",
                                                                    opacity: savingField === item.field ? 0.5 : 1
                                                                }}
                                                                title={isAck ? "Undo Acknowledgment" : "Acknowledge Discrepancy"}
                                                            >
                                                                {savingField === item.field ? <Loader2 size={12} className="spinning" color="currentColor" /> : <CheckCircle2 size={14} />}
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Flags */}
                    {result.flags && result.flags.length > 0 && (
                        <div>
                            <h4 style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-secondary)", marginBottom: "8px" }}>
                                Flags & Notes
                            </h4>
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                {result.flags.map((flag, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            display: "flex", alignItems: "flex-start", gap: "6px",
                                            padding: "8px 10px", borderRadius: "6px",
                                            background: "rgba(245,158,11,0.05)", border: "1px solid rgba(245,158,11,0.12)",
                                            fontSize: "0.78rem", color: "var(--text-secondary)", lineHeight: 1.4,
                                        }}
                                    >
                                        <FileText size={12} color="#f59e0b" style={{ flexShrink: 0, marginTop: "2px" }} />
                                        {flag}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="modal-footer" style={{ padding: "12px 20px", borderTop: "1px solid var(--border-color)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: "0.68rem", color: "var(--text-muted)", fontStyle: "italic", flex: 1, marginRight: "16px" }}>
                        AI-powered analysis. Verify all figures with actual payment schedule.
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                        {canRetry && (
                            <button
                                className="btn btn-primary"
                                style={{ fontSize: "0.85rem", padding: "6px 12px" }}
                                onClick={onRetry}
                            >
                                Run the check again
                            </button>
                        )}
                        {onReupload && (
                            <button
                                className="btn"
                                style={{
                                    background: "var(--bg-secondary)",
                                    border: "1px solid var(--border-color)",
                                    color: "var(--text-primary)",
                                    fontSize: "0.85rem",
                                    padding: "6px 12px"
                                }}
                                onClick={onReupload}
                            >
                                Upload New File
                            </button>
                        )}
                        <button className="btn btn-secondary" onClick={onClose} style={{ padding: "6px 16px" }}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
